import { DestroyRef, Injectable, computed, effect, inject, untracked } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { LocaleService } from '../../../../../core/services/locale.service';
import { LocationStateService } from '../../../../../core/services/location-state.service';
import { ShellStateService } from '../../../../../core/services/shell-state.service';
import { ViewerIdentityService } from '../../../../../core/services/viewer-identity.service';
import { FeatureFlagsViewerService } from '../../../../../core/services/feature-flags-viewer.service';
import { MapWorkspaceService } from '../../../../../core/services/map-workspace.service';
import { rank } from '../models/cmdk-ranking';
import { dropWorkspaceDuplicates, orderGroupsByMatch } from '../models/cmdk-blend';
import type { CmdkScored } from '../models/cmdk-ranking';
import { buildRows, groupByKind } from '../models/cmdk-rows';
import type { CmdkGroupBlock } from '../models/cmdk-rows';
import { CMDK_GROUP_ORDER, CMDK_MAP_GROUP_ORDER, CMDK_SCOPES, kindGroup, scopeKind } from '../models/cmdk.model';
import type { CmdkAskItem, CmdkGroup, CmdkItem, CmdkRenderRow, CmdkScope, CmdkScopeHint } from '../models/cmdk.model';
import type { CmdkProvider, CmdkProviderContext, CmdkRemoteProvider } from '../models/cmdk-provider';
import { shouldRunProvider } from '../models/cmdk-provider';
import { CmdkStateService } from './cmdk-state.service';
import { CmdkActionProvider } from './cmdk-providers/action.provider';
import { CmdkAppProvider } from './cmdk-providers/app.provider';
import { contextBoostFor } from './cmdk-providers/command.source';
import { CmdkNavProvider } from './cmdk-providers/nav.provider';
import { CmdkRecentsProvider } from './cmdk-providers/recents.provider';
import { CmdkNewsProvider } from './cmdk-providers/news.provider';
import { CmdkPersonProvider } from './cmdk-providers/person.provider';
import { CmdkSpaceProvider } from './cmdk-providers/space.provider';

/**
 * RESPONSIBILITY: the only thing in the palette that decides WHAT ROWS EXIST.
 * It holds the provider registry, gathers their items, ranks them, groups them
 * and flattens the result into the list the pane renders.
 *
 * SIGNALS:
 *   rows          — the flat render list, headers and states included
 *   selectable    — just the selectable rows, in flat order
 *   selectedItem  — the item under the current selection index
 *   resultCount   — how many selectable rows there are
 *
 * USED BY: CmdkResultsPaneComponent, CmdkPreviewPaneComponent, CmdkComponent.
 *
 * NOT its job: navigating, mutating, or anything to do with the AI. A provider
 * answers one question and this service asks it.
 *
 * AI CONTEXT — the recompute budget
 *
 * The legacy palette recomputes on every keystroke AND on every registry
 * mutation, because `registry.search()` is called inside a `computed`
 * (`command-palette.component.ts:90`). `MapCommandsService` has three
 * `effect()`-driven re-registrations (themes, floors, locations) that can
 * therefore retrigger scoring mid-typing for reasons the user cannot see.
 *
 * This service reads the registry inside `untracked()` for exactly that
 * reason: the row list depends on the QUERY, not on when a contributor last
 * re-registered. The trade is that a command registered while the palette is
 * open does not appear until the next keystroke — which is the correct
 * trade for a surface the user is typing into.
 *
 * Remote providers, their debounce and their per-group `ApiState` arrive in
 * slice 5 (SPEC-command-k.md §8). Everything registered here today is local
 * and synchronous, so there is no network path yet to get wrong.
 */
/**
 * A signal that is never aborted, for the synchronous context. Allocated once
 * rather than per recompute: the previous `new AbortController().signal`
 * inside the `context` computed built a fresh controller on every keystroke
 * that nobody held a reference to and nobody could ever abort.
 */
const NEVER_ABORTED = new AbortController().signal;

@Injectable({ providedIn: 'root' })
export class CmdkSearchService {
  private readonly state = inject(CmdkStateService);
  private readonly shell = inject(ShellStateService);
  private readonly identity = inject(ViewerIdentityService);
  private readonly featureFlags = inject(FeatureFlagsViewerService);
  private readonly mapWorkspace = inject(MapWorkspaceService);
  private readonly locationState = inject(LocationStateService);
  private readonly transloco = inject(TranslocoService);
  private readonly locale = inject(LocaleService);

  private readonly navProvider = inject(CmdkNavProvider);
  private readonly actionProvider = inject(CmdkActionProvider);
  private readonly appProvider = inject(CmdkAppProvider);
  private readonly recentsProvider = inject(CmdkRecentsProvider);
  private readonly spaceProvider = inject(CmdkSpaceProvider);
  private readonly personProvider = inject(CmdkPersonProvider);
  private readonly newsProvider = inject(CmdkNewsProvider);
  private readonly destroyRef = inject(DestroyRef);

  /**
   * Ordered so a kind can be added or removed as one line plus one file. The
   * order here does not affect display — `CMDK_GROUP_ORDER` does — but it does
   * fix the sequence local providers run in, which keeps ties deterministic.
   */
  private readonly providers: readonly CmdkProvider[] = [
    this.actionProvider,
    this.navProvider,
    this.appProvider,
    // LAST, deliberately. The gather de-duplicates on `kind:id` and the first
    // writer wins, so a recent that is also a live result loses to the live
    // one — which has today's availability where the recent deliberately has
    // none.
    this.recentsProvider,
  ];

  /**
   * Remote providers are a SEPARATE list, and the separation is structural.
   *
   * `items()` iterates `providers` inside a `computed()`. A remote provider in
   * that array would fire an undebounced, uncancellable request from inside a
   * reactive read — once per recompute, which is once per keystroke and again
   * on every unrelated signal the computed happens to touch. These are only
   * ever called from `scheduleRemote()`, and the `CmdkRemoteProvider` type
   * exists so the two cannot be mixed up by accident.
   */
  private readonly remoteProviders: readonly CmdkRemoteProvider[] = [
    this.spaceProvider,
    this.personProvider,
    this.newsProvider,
  ];

  /**
   * How many rows a group contributes when the query is empty. Browsing a
   * chip is uncapped — that is the point of browsing — but the default view
   * is a shortlist, not a catalogue dump.
   */
  private readonly DEFAULT_LIMIT = 6;

  /** Debounce before a remote request leaves. See `scheduleRemote`. */
  private readonly REMOTE_DEBOUNCE_MS = 220;

  private debounceTimer: ReturnType<typeof setTimeout> | null = null;
  /** Aborted whenever the request key changes; its signal is the discard token. */
  private generation: AbortController | null = null;
  /**
   * A retry's own discard token. It used to replace `generation`, which
   * aborted every OTHER provider's request in flight — and an aborted load
   * returns without touching its state, so those groups sat on a skeleton
   * until the next keystroke.
   */
  private retryGeneration: AbortController | null = null;

  /**
   * The ONE thing the scheduler effect tracks.
   *
   * A string, deliberately: Angular dedupes computeds with `Object.is`, so a
   * string key is `distinctUntilChanged` for free. A keystroke that leaves the
   * trimmed query unchanged, a command re-registering, a re-render — none of
   * them produce a new key, so none of them reach the network.
   *
   * `section` and `userActions` are NOT in the key. They are request PAYLOAD,
   * not triggers: including them means a route change or a permissions refresh
   * fires a burst of searches in the middle of someone typing.
   *
   * Nor is the SCOPE (search relook, 9 Oct 2026). Every remote source runs
   * whichever chip is active — the same requests the All chip sends — and the
   * chip only decides what is SHOWN. That is what lets an empty Spaces tab say
   * "2 matches in People" instead of only offering Ask, and it makes
   * switching chips instant: the answers are already here.
   */
  private readonly requestKey = computed(() => {
    if (!this.state.open()) return '';
    return [
      this.state.effectiveQuery(),
      this.focusedLocationId() ?? '',
    ].join('\x00');
  });

  constructor() {
    // The scheduler. Everything it reads apart from `requestKey()` is read
    // inside `untracked()`, because it also WRITES provider state — and a
    // tracked read of any signal it then writes is the exact shape that froze
    // the app in `43fa13ba`.
    //
    // Reading `provider.state.*` here would be the worst case: `empty` is a
    // computed over data, loading AND error, so one read enrols all three and
    // the very next `setLoading()` re-dirties the effect forever. None of the
    // three ratchet metrics can see it either — they match `X.set(`/`X.update(`
    // and every write here goes through `setData()`/`setLoading()`. So this
    // guard is the only thing standing between us and a green-CI freeze.
    effect(() => {
      const key = this.requestKey();
      untracked(() => this.scheduleRemote(key));
    });

    this.destroyRef.onDestroy(() => {
      if (this.debounceTimer !== null) clearTimeout(this.debounceTimer);
      this.generation?.abort();
    });
  }

  /**
   * Decide which remote providers should run for this key, mark them loading
   * immediately, and fire the requests after the debounce.
   *
   * Loading is set BEFORE the debounce on purpose. The skeleton should appear
   * when we know a fetch is coming, not 220ms later when it actually leaves —
   * otherwise the group pops in from nothing and the list jumps under the
   * user's selection.
   *
   * Called only from inside `untracked()`.
   */
  private scheduleRemote(key: string): void {
    if (this.debounceTimer !== null) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }
    // Everything in flight is now answering a question nobody asked.
    this.generation?.abort();
    this.retryGeneration?.abort();
    this.retryGeneration = null;

    if (!key) {
      for (const provider of this.remoteProviders) provider.standDown();
      this.generation = null;
      return;
    }

    const generation = new AbortController();
    this.generation = generation;
    const ctx: CmdkProviderContext = { ...this.context(), signal: generation.signal };

    // Scope-blind on purpose — see `requestKey`.
    const running = this.remoteProviders.filter(p => shouldRunProvider(p, { ...ctx, scope: 'all' }));
    for (const provider of this.remoteProviders) {
      if (running.includes(provider)) provider.markLoading();
      // Not just "no results" — this group should not be on screen at all,
      // so it loses its header too.
      else provider.standDown();
    }
    if (running.length === 0) return;

    this.debounceTimer = setTimeout(() => {
      this.debounceTimer = null;
      if (generation.signal.aborted) return;
      for (const provider of running) {
        // Each provider owns its own state writes and its own staleness check
        // — see CmdkRemoteProviderBase. A rejection here would be a bug in the
        // provider rather than a failed request, which `load` already maps to
        // an error state, so it is logged rather than swallowed.
        void provider.load(ctx).catch((cause: unknown) => {
          console.warn(`[CmdkSearchService] ${provider.id} provider threw:`, cause);
        });
      }
    }, this.REMOTE_DEBOUNCE_MS);
  }

  /** The building searched: the harness's, else the one the app has focused. */
  private readonly focusedLocationId = computed(() => {
    const harness = this.state.harness();
    return harness ? harness.locationId : this.locationState.selectedLocationId();
  });

  private readonly context = computed<CmdkProviderContext>(() => ({
    query: this.state.effectiveQuery(),
    scope: this.state.scope(),
    section: this.shell.currentSection() ?? 'map',
    locationId: this.focusedLocationId(),
    floorId: this.locationState.selectedFloorId(),
    userActions: this.state.harness()?.userActions ?? this.identity.actions(),
    // Local providers are synchronous, so nothing can outlive the call. The
    // remote path never uses THIS signal — `scheduleRemote()` builds its own
    // context carrying the live generation's signal. This one exists only so
    // the type is satisfied for the synchronous callers.
    signal: NEVER_ABORTED,
  }));

  /** Localised group headers. Re-evaluates on locale change. */
  private readonly groupLabels = computed<Readonly<Record<CmdkGroup, string>>>(() => {
    this.locale.locale();
    const t = (key: string): string => this.transloco.translate(key);
    return {
      suggested: t('shell.cmdk.group.suggested'),
      'go-to': t('shell.cmdk.group.go_to'),
      spaces: t('shell.cmdk.group.spaces'),
      people: t('shell.cmdk.group.people'),
      apps: t('shell.cmdk.group.apps'),
      spotlights: t('spotlight.places.search.group'),
      news: t('shell.cmdk.group.news'),
      recent: t('shell.cmdk.group.recent'),
      'not-in-filter': t('shell.cmdk.group.not_in_filter'),
    };
  });

  /** Every item every enabled provider offers for the current context. */
  private readonly items = computed<CmdkItem[]>(() => {
    if (!this.state.open()) return [];
    const ctx = this.context();

    // Read the ASYNC sources' signals inside the tracked scope, before the
    // untracked block. `QuickLinksService.allLinks()` resolves after its own
    // network load, and the whole provider loop used to sit inside
    // `untracked()` — so a palette opened during that load computed an empty
    // Apps group and never recomputed when the links arrived. The user saw
    // "no results" for apps they own.
    //
    // The registry stays untracked (see the class docs); only genuinely
    // asynchronous inputs are enrolled.
    this.appProvider.revision();

    return untracked(() => {
      // Deduplicated on kind + id as we go.
      //
      // This is not defensive tidiness. `CommandRegistryService.registerAll`
      // appends without checking ids, and every contributor holds a disposer
      // it is trusted to call — so a service that re-registers on a signal
      // change (MapCommandsService does it three times) can leave the same
      // command in the array twice. The legacy palette renders both, and two
      // identical rows in a keyboard-driven list is worse than it looks: the
      // ⌘-digits then point at the same thing twice and the count lies.
      //
      // Keyed on kind AND id because ids are only unique within a source — a
      // space and a person can legitimately share one.
      const seen = new Set<string>();
      const collected: CmdkItem[] = [];
      for (const provider of this.providers) {
        if (!shouldRunProvider(provider, ctx)) continue;
        const result = ctx.query.length === 0 ? provider.browse(ctx) : provider.search(ctx);
        // Local providers are synchronous by contract; a promise here is a
        // programming error in the provider rather than a runtime condition.
        if (!Array.isArray(result)) continue;
        for (const item of result) {
          const key = `${item.kind}:${item.id}`;
          if (seen.has(key)) continue;
          seen.add(key);
          collected.push(item);
        }
      }
      return dropWorkspaceDuplicates(collected, ctx.scope);
    });
  });

  /**
   * Local matches in the scopes the chip is NOT showing, for the empty-tab
   * hint. Only computed while a chip narrows a query — the All chip already
   * shows them — and it is the same ranking `items` gets, just scope-blind.
   */
  private readonly hiddenLocalCounts = computed<ReadonlyMap<CmdkGroup, number>>(() => {
    if (!this.state.open() || !this.state.hasScope() || !this.state.hasQuery()) return new Map();
    const ctx = { ...this.context(), scope: 'all' as CmdkScope };
    this.appProvider.revision();
    return untracked(() => {
      const items: CmdkItem[] = [];
      for (const provider of this.providers) {
        if (provider === this.recentsProvider || !shouldRunProvider(provider, ctx)) continue;
        const result = provider.search(ctx);
        if (Array.isArray(result)) items.push(...result);
      }
      const counts = new Map<CmdkGroup, number>();
      for (const [group, list] of groupByKind(rank(dropWorkspaceDuplicates(items, 'all'), ctx.query))) {
        counts.set(group, list.length);
      }
      return counts;
    });
  });

  /**
   * The registry's `contextBoost` as a ranking boost.
   *
   * The providers no longer let the registry MATCH (see `visibleCommands`),
   * but the registry is still the authority on RELEVANCE: it is what knows
   * that the Map workspace matters more while you are on the map. Losing that
   * signal was the cost of taking matching back, so it is re-applied here —
   * clamped into the ±20 window, where it reorders within a tier and never
   * across one.
   */
  private readonly boost = computed(() => {
    const ctx = this.context();
    return (item: CmdkItem): number =>
      item.kind === 'nav' || item.kind === 'action' ? contextBoostFor(item.command, ctx) : 0;
  });

  private readonly ranked = computed<CmdkScored[]>(() =>
    rank(this.items(), this.state.effectiveQuery(), { boost: this.boost() }),
  );

  /** The grouped blocks, capped for the default view. */
  private readonly blocks = computed<CmdkGroupBlock[]>(() => {
    const labels = this.groupLabels();
    const grouped = groupByKind(this.ranked());
    const uncapped = this.state.hasScope() || this.state.hasQuery();

    // Under a chip, only that chip's group is shown — the remote sources run
    // regardless (see `requestKey`), and their answers feed the hint instead.
    const shown = this.scopeGroup();

    const out: CmdkGroupBlock[] = [];
    for (const group of CMDK_GROUP_ORDER) {
      if (shown && group !== shown) continue;
      const remote = this.remoteForGroup(group);
      const items = remote ? (remote.state.data() ?? []) : grouped.get(group);
      const failed = this.groupFailed(group);

      // A failed group still gets a header and a banner — one dead source must
      // never look like "there is nothing here". Apps can fail while still
      // holding Workplace's own apps, which come from no network at all:
      // those rows stay, with the banner after them.
      if (failed) {
        out.push({ group, label: labels[group], items: items ?? [], failed: true });
        continue;
      }

      // A group whose request is in flight emits a block with `loading` even
      // though it has no items yet. Without this the block was skipped by the
      // length check below, `buildRows` never saw `loading: true`, and the
      // "no matches" rule fired over a search that had not come back — the
      // palette said "nothing found" and then quietly filled in.
      if (remote && this.groupLoading(group)) {
        out.push({ group, label: labels[group], items: [], loading: true });
        continue;
      }

      if (!items || items.length === 0) continue;

      out.push({
        group,
        label: labels[group],
        // Remote groups are already limited and already ranked by the backend
        // — re-slicing them with the browse cap would drop results the server
        // considered the best matches.
        items: remote || uncapped ? items : items.slice(0, this.DEFAULT_LIMIT),
      });
    }
    return out;
  });

  /** The one group a chip shows, or null under All. */
  private readonly scopeGroup = computed<CmdkGroup | null>(() => {
    const kind = scopeKind(this.state.scope());
    return kind ? kindGroup(kind) : null;
  });

  /**
   * "2 matches in People" — what the other tabs found, for a chip whose own
   * group came back empty. Remote counts are the rows each source returned
   * (it is capped, so "8" can mean "8 or more"); a source still loading is
   * left out rather than guessed at.
   */
  readonly scopeHints = computed<CmdkScopeHint[]>(() => {
    if (!this.state.hasScope() || !this.state.hasQuery()) return [];
    const current = this.state.scope();
    const local = this.hiddenLocalCounts();
    const labels = this.groupLabels();
    const hints: CmdkScopeHint[] = [];
    for (const scope of CMDK_SCOPES) {
      if (scope === 'all' || scope === current) continue;
      const kind = scopeKind(scope);
      if (!kind) continue;
      const group = kindGroup(kind);
      const remote = this.remoteForGroup(group);
      const count = remote
        ? remote.state.loading() ? 0 : (remote.state.data() ?? []).length
        : (local.get(group) ?? 0);
      if (count > 0) hints.push({ scope, group, label: labels[group], count });
    }
    return hints;
  });

  /** True while any remote source is fetching — the auto-ask waits for it. */
  readonly anyRemoteLoading = computed(() => this.remoteProviders.some(p => p.state.loading()));

  /**
   * Whether a group's source failed.
   *
   * Apps is the one that matters today: on an org-link failure
   * `QuickLinksService` sets its error AND substitutes six hardcoded fallback
   * links, so a palette reading only `allLinks()` would confidently offer
   * SharePoint, ServiceNow, Jira and friends to a tenant that may own none of
   * them. Showing the banner instead is the honest reading.
   */
  private groupFailed(group: CmdkGroup): boolean {
    if (group === 'apps') {
      if (!shouldRunProvider(this.appProvider, this.context())) return false;
      return this.appProvider.hasError();
    }
    const remote = this.remoteForGroup(group);
    return remote ? remote.state.error() !== null : false;
  }

  /** True while a remote group's request is in flight. */
  private groupLoading(group: CmdkGroup): boolean {
    return this.remoteForGroup(group)?.state.loading() ?? false;
  }

  /** The remote provider that owns a group, if any. */
  private remoteForGroup(group: CmdkGroup): CmdkRemoteProvider | undefined {
    return this.remoteProviders.find(p => kindGroup(p.kind) === group);
  }

  /**
   * The Ask row, or null.
   *
   * Synthesised per query — never fetched, never cached. Absent entirely when
   * the tenant has no AI, which is what makes the AI-off state a real
   * degradation rather than a disabled affordance: no row, no ⌘↵ hint, no
   * mention in the footer.
   *
   * WHERE it lands is `buildRows`' decision, not this one.
   */
  private readonly askItem = computed<CmdkAskItem | null>(() => {
    if (!this.featureFlags.isEnabled('ai')()) return null;
    const query = this.state.effectiveQuery();
    if (!query) return null;
    return {
      id: 'ask',
      kind: 'ask',
      title: query,
      query,
      scopedTo: this.state.hasScope() ? this.state.scope() : null,
    };
  });

  /** On the map with the map workspace on, the nav's search is the map's: this site's spaces and people lead. */
  private readonly mapFirst = computed(() => this.shell.currentSection() === 'map' && this.mapWorkspace.enabled());

  /** The flat render list. */
  readonly rows = computed<CmdkRenderRow[]>(() => {
    const ask = this.askItem();
    const query = this.state.effectiveQuery();
    const baseOrder = this.mapFirst() ? CMDK_MAP_GROUP_ORDER : CMDK_GROUP_ORDER;
    const blocks = this.blocks();
    return buildRows(blocks, {
      scoped: this.state.hasScope(),
      query,
      ask: ask ? { item: ask, notInFilterLabel: this.groupLabels()['not-in-filter'] } : null,
      groupOrder: this.state.hasScope() || !query ? baseOrder : orderGroupsByMatch(blocks, query, baseOrder),
    });
  });

  /**
   * The hints to show: a chip is active, its tab settled EMPTY (nothing still
   * loading, no error, no row but Ask), and another tab has matches. Rendered
   * above the list, outside the listbox, as buttons that switch the chip.
   */
  readonly emptyTabHints = computed<CmdkScopeHint[]>(() => {
    if (!this.state.hasScope()) return [];
    const rows = this.rows();
    if (rows.some(r => r.type === 'skeleton' || r.type === 'error')) return [];
    if (rows.some(r => r.type === 'row' && r.item.kind !== 'ask')) return [];
    return this.scopeHints();
  });

  readonly selectable = computed(() =>
    this.rows().filter((r): r is Extract<CmdkRenderRow, { type: 'row' }> => r.type === 'row'),
  );

  readonly resultCount = computed(() => this.selectable().length);

  /** The item under the selection, or undefined when the list is empty. */
  readonly selectedItem = computed<CmdkItem | undefined>(
    () => this.selectable()[this.state.selectedIndex()]?.item,
  );

  /** The DOM id for `aria-activedescendant`. */
  readonly activeDescendantId = computed<string | null>(
    () => this.selectable()[this.state.selectedIndex()]?.domId ?? null,
  );

  /**
   * Reload a group whose source failed.
   *
   * The error state renders a Try-again affordance and a dead one is worse
   * than none — it tells the user the data is genuinely gone rather than that
   * a request needs repeating. So every group that can render the banner must
   * be reachable from here; when this only knew about Apps, the Spaces and
   * People banners had a button that did nothing.
   *
   * The remote retry re-runs the CURRENT context rather than the one that
   * failed. That is deliberate: by the time someone presses Try again the
   * query may have moved on, and re-issuing the stale one would replace a
   * banner with results for something they are no longer looking for.
   */
  retryGroup(group: CmdkGroup): void {
    if (group === 'apps') {
      void this.appProvider.reload();
      return;
    }
    const remote = this.remoteForGroup(group);
    if (!remote) return;

    untracked(() => {
      const generation = new AbortController();
      this.retryGeneration?.abort();
      this.retryGeneration = generation;
      const ctx: CmdkProviderContext = { ...this.context(), signal: generation.signal };
      if (!shouldRunProvider(remote, ctx)) return;
      remote.markLoading();
      void remote.load(ctx).catch((cause: unknown) => {
        console.warn(`[CmdkSearchService] ${remote.id} retry threw:`, cause);
      });
    });
  }

  /** The top result, for the ghost completion. */
  readonly topItem = computed<CmdkItem | undefined>(() => this.selectable()[0]?.item);
}

